import { trim } from 'lodash';
import { createValueDefinitionKey } from '@common/i18n/createKey';
import { type InstallationFull, type InstallationInfo } from '@common/types/api';
import {
  type DateTimeUTC,
  type DeviceId,
  type IanaId,
  type InstallationId,
} from '@common/types/brandedTypes';
import { type ContractType } from '@common/types/models/Contract';
import { parseTimestampAsUtc } from '@common/utils/datetime/isoTimestamp';
import { IanaIdUtc } from '@common/utils/datetime/timezones';

export const EmergencyModeValue = 'NOTBETRIEB';

type ConnectionStatus = 'online' | 'offline' | 'never';

export interface BaseDevice {
  readonly id: InstallationId;
  readonly pid: number;
  readonly deviceId: DeviceId;
  readonly name: string;
  readonly lastConnectedAt?: DateTimeUTC;
  readonly connectionStatus: ConnectionStatus;
  readonly typeId: string;
  readonly iconPath: string;
  readonly picturePath: string;
  readonly timeZoneId: string;
  readonly ianaId: IanaId;
  readonly dstLessIanaId: IanaId;
  readonly coordinates?: { lat: number; lng: number; };
  readonly deviceType: string;
  readonly extension: string;
  readonly controllerUnit: string;
  readonly macAddress?: string;
  readonly operationMode?: string;
  readonly communicationFirmwareVersion?: string;
  readonly controllerFirmwareVersion?: string;
  readonly contractType?: ContractType;
  readonly registerValues: Record<number, number>;
  readonly cascadeCount?: number;
  readonly pendingDeletion?: {
    deadline: DateTimeUTC;
    by: string;
    created: DateTimeUTC;
    force?: boolean;
  };
}

export const createBaseDeviceFromResponse = (
  response: InstallationFull | InstallationInfo,
): BaseDevice => {
  const apiDevice = response as InstallationFull & InstallationInfo;

  const lastConnectedAt = apiDevice.lastOnlineUtc
    ? parseTimestampAsUtc(apiDevice.lastOnlineUtc)
    : undefined;
  let connectionStatus: ConnectionStatus = 'never';
  if (lastConnectedAt) {
    connectionStatus = apiDevice.isOnline ? 'online' : 'offline';
  }
  let { contractType } = apiDevice;
  if (contractType) {
    contractType = {
      ...contractType,
      name: contractType.name.replace('[[', '').replace(']]', ''),
    };
  }
  const typeId = `${apiDevice.profile?.id ?? apiDevice.installationProfileId}`;
  const imageName = apiDevice.deviceType?.replace(/\s/g, '-');
  const registerValues = (apiDevice.registerValues ?? [])
    .reduce<BaseDevice['registerValues']>((accumulator, value) => ({
      ...accumulator,
      [value.registerIndex]: value.displayValue,
    }), {});
  return {
    typeId,
    contractType,
    connectionStatus,
    lastConnectedAt,
    registerValues,
    id: apiDevice.id as InstallationId,
    pid: apiDevice.pid,
    deviceId: apiDevice.deviceId,
    name: trim(apiDevice.name),
    iconPath: `/assets/external/device/icons/${imageName}-small.png`,
    picturePath: `/assets/external/device/pictures/${imageName}.png`,
    timeZoneId: apiDevice.timeZone ?? apiDevice.timeZoneId,
    ianaId: IanaIdUtc,
    dstLessIanaId: IanaIdUtc,
    coordinates: apiDevice.coordinates
      ? { lat: apiDevice.coordinates.latitude, lng: apiDevice.coordinates.longitude }
      : undefined,
    deviceType: createValueDefinitionKey(apiDevice.deviceType),
    extension: createValueDefinitionKey(apiDevice.deviceExtension),
    controllerUnit: createValueDefinitionKey(apiDevice.controllerUnit),
    macAddress: apiDevice.macAddress,
    operationMode: apiDevice.deviceOperationMode,
    communicationFirmwareVersion: apiDevice.firmware?.normalizedCommunicationFirmwareVersion,
    controllerFirmwareVersion: apiDevice.firmware?.normalizedFirmwareVersion,
    cascadeCount: apiDevice.cascade?.count,
    pendingDeletion: apiDevice.pendingDeviceDeletion
      ? {
        deadline: parseTimestampAsUtc(apiDevice.pendingDeviceDeletion.scheduledUtc),
        by: apiDevice.pendingDeviceDeletion.createdByUser.email,
        created: parseTimestampAsUtc(apiDevice.pendingDeviceDeletion.createdUtc),
        force: apiDevice.pendingDeviceDeletion.isForceDeletion,
      }
      : undefined,
  };
};
